import { adaptPlainObjectToCamelCase } from '@lib/commons/util-functions/plain-object-to-pascal.function';

import {
  GenericServiceValue,
  PersonalDoctorMetadata,
  PersonalDoctorValue,
  PharmacologicalItemMetaData,
  PharmacologicalTreatmentValue,
  PrescriptionFormValue,
  PrescriptionItem,
  PrescriptionTypes,
  ServiceMetadata,
} from '../models/prescription.interface';

type DraftFormValue = PrescriptionFormValue<PharmacologicalTreatmentValue | GenericServiceValue | PersonalDoctorValue>;
type ItemMetadata = ServiceMetadata | PharmacologicalItemMetaData | PersonalDoctorMetadata;

export function convertDraftMetadata(draft: DraftFormValue): ItemMetadata {
  const metadata = draft.metadata;

  switch (draft.type) {
    case PrescriptionTypes.Drugs: {
      const pharmMetadata = metadata as PharmacologicalTreatmentValue;
      return {
        single_dose: pharmMetadata.singleDose.toString(),
        form: pharmMetadata.form.toString(),
        frequency: pharmMetadata.frequency,
        treatment_dose: pharmMetadata.treatmentDose.toString(),
        daily_amount: pharmMetadata.dailyAmount.toString(),
        duration: pharmMetadata.duration.toString(),
        inn: pharmMetadata.inn,
        inn_ua: pharmMetadata.morionSubstance,
        trademarks: JSON.stringify(adaptPlainObjectToCamelCase(pharmMetadata.morionItems)),
      } as PharmacologicalItemMetaData;
    }

    case PrescriptionTypes.PersonalDoctorAppointment: {
      const doctorMetadata = metadata as PersonalDoctorValue;
      return {
        service_id: doctorMetadata.serviceId,
        doctor_id: doctorMetadata.doctorId,
        doctor_partner_id: doctorMetadata.doctorPartnerId ?? null,
      } as PersonalDoctorMetadata;
    }

    case PrescriptionTypes.RelatedSpecialists:
    case PrescriptionTypes.LaboratoryService:
    case PrescriptionTypes.InstrumentalDiagnostic:
    default: {
      const serviceMetadata = metadata as GenericServiceValue;
      return {
        code: serviceMetadata.code,
        quantity: serviceMetadata.quantity.toString(),
      } as ServiceMetadata;
    }
  }
}

export function convertDraftToItem(draft: DraftFormValue): PrescriptionItem<ItemMetadata> {
  return {
    id: draft.id,
    name: draft.name,
    type: draft.type,
    startDate: draft.startDate,
    endDate: draft.endDate,
    metadata: convertDraftMetadata(draft),
    guaranteeStatus: draft.guaranteeStatus,
    reason: draft.reason,
    statusReason: draft.statusReason,
    completedInAppointment: draft.completedInAppointment,
    completionReason: draft.completionReason,
    franchise: draft.franchise,
    sendGuaranteeNotification: draft.sendGuaranteeNotification,
    guaranteeId: draft.guaranteeId,
    originalPrescriptionId: draft.originalPrescriptionId,
  } as PrescriptionItem<ItemMetadata>;
}

export function convertDraftsToItems(drafts: DraftFormValue[]): PrescriptionItem<ItemMetadata>[] {
  return drafts
    .filter(draft => draft.name && draft.type)
    .map(draft => convertDraftToItem(draft));
}
